/**
 * The router contexts a tab that does NOT own the browser URL should see.
 *
 * Without these, a page component calling `useNavigate()` — Study redirecting
 * to its channel list on mount, say — would change the browser URL, and every
 * other tab would follow it.
 *
 * Uses React Router's UNSAFE_NavigationContext, UNSAFE_LocationContext and
 * UNSAFE_RouteContext to synthesise a navigator and location scoped to one tab.
 * `TabRouterContext` renders the providers; this only builds the values, so
 * that the same three providers can serve a passthrough tab without the
 * element tree changing shape.
 *
 * Bug 3 fix (spec 2026-05-24): preserves search and hash everywhere
 * (synthetic Location, navigator push/replace, encodeLocation, createHref,
 * route context). Handles search-only ('?...') and hash-only ('#...')
 * string-form `to` correctly.
 */
import { useCallback, useLayoutEffect, useMemo, useRef, type ContextType } from 'react';

import {
  UNSAFE_NavigationContext,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
} from 'react-router-dom';

import { splitFullPath } from './helpers/splitFullPath';
import { useWorkspaceStore } from './stores/useWorkspaceStore';
import { titleForPath } from './titleForPath';

import type { Location, Navigator, NavigationType } from 'react-router-dom';

/** What one tab's synthesised router needs to know about it. */
export interface PaneRouterInput {
  tabId: string;
  /** Full tab.path (pathname + optional search + optional hash). */
  pathname: string;
  params: Record<string, string>;
  /** The tab's stored `location.state`, served back to the page unchanged. */
  state?: unknown;
  /** The tab's stored `location.key`; the tab id stands in until it navigates. */
  locationKey?: string | undefined;
}

/**
 * Compose a full URL fragment from an object-form `to` ({ pathname?, search?, hash? }),
 * filling in missing parts from the current full path (pathname + search + hash).
 */
function composeFullFromObject(
  to: { pathname?: string; search?: string; hash?: string },
  currentFullPath: string
): string {
  const { pathname: curPathname, search: curSearch } = splitFullPath(currentFullPath);
  const pathname = to.pathname ?? curPathname;
  // If `to` is a resolved hash-only navigation on the same pathname (resolveTo
  // produces { pathname, search: '', hash } for `navigate('#anchor')`), preserve
  // the current search. Otherwise the `??` would clobber it.
  const isHashOnlyOnSamePath = pathname === curPathname && Boolean(to.hash) && !to.search;
  const search = isHashOnlyOnSamePath ? curSearch : (to.search ?? '');
  const hash = to.hash ?? '';
  return `${pathname}${search}${hash}`;
}

/**
 * Resolve a string-form `to` against the current pathname, handling search-only
 * ('?...') and hash-only ('#...') forms correctly.
 *
 * React Router synthesizes search-only `to` when application code calls
 * setSearchParams() — those must NOT be parsed as path segments.
 */
function resolveStringTo(to: string, currentFullPath: string): string {
  if (to.startsWith('/')) return to; // absolute
  if (to.startsWith('?')) {
    // search-only: keep current pathname, replace search
    const { pathname } = splitFullPath(currentFullPath);
    return `${pathname}${to}`;
  }
  if (to.startsWith('#')) {
    // hash-only: keep current pathname + search, replace hash
    const { pathname, search } = splitFullPath(currentFullPath);
    return `${pathname}${search}${to}`;
  }
  // relative path: resolve against current pathname
  return resolveRelativePath(splitFullPath(currentFullPath).pathname, to);
}

/** Resolve a relative path segment (e.g., '../foo' or 'bar') against a base pathname. */
function resolveRelativePath(base: string, relative: string): string {
  const baseParts = base.split('/').filter(Boolean);
  baseParts.pop();
  const relParts = relative.split('/').filter(Boolean);
  for (const part of relParts) {
    if (part === '..') baseParts.pop();
    else if (part !== '.') baseParts.push(part);
  }
  return '/' + baseParts.join('/');
}

/** The synthesised navigation, location and route contexts for one tab. */
export interface PaneRouterValues {
  navigation: ContextType<typeof UNSAFE_NavigationContext>;
  location: ContextType<typeof UNSAFE_LocationContext>;
  route: ContextType<typeof UNSAFE_RouteContext>;
}

/**
 * Build the contexts a tab that does NOT own the browser URL should see.
 *
 * A hook rather than a component so the caller can render the same three
 * providers whether it uses these values or the real ones. Swapping which
 * component wraps a tab would change its shape in the element tree and remount
 * it — the one thing the live-tabs design exists to prevent, and a thing that
 * stays invisible until two tabs are mounted at once.
 */
export function usePaneRouterValues({
  tabId,
  pathname,
  params,
  state,
  locationKey,
}: PaneRouterInput): PaneRouterValues {
  const updateTab = useWorkspaceStore(s => s.updateTab);
  const basename = '';

  const pathnameRef = useRef(pathname);
  useLayoutEffect(() => { pathnameRef.current = pathname; });

  const parsed = useMemo(() => splitFullPath(pathname), [pathname]);

  const handleNavigate = useCallback(
    (to: string, navigationState?: unknown) => {
      const resolved = resolveStringTo(to, pathnameRef.current);

      // Update the tab title to match the new path (pathname only — titleForPath breaks on '?').
      const newTitle = titleForPath(splitFullPath(resolved).pathname);
      // The state rides along. Dropping it here is what made a journal search
      // deep-link open the date and lose which entry it meant.
      updateTab(tabId, { title: newTitle, path: resolved, state: navigationState });
    },
    [tabId, updateTab]
  );

  const navigator = useMemo<Navigator>(
    () => ({
      createHref: to => {
        if (typeof to === 'string') return to;
        return composeFullFromObject(to, pathnameRef.current);
      },
      go: () => {},
      push: (to, navigationState) => {
        const full =
          typeof to === 'string' ? to : composeFullFromObject(to, pathnameRef.current);
        handleNavigate(full, navigationState);
      },
      replace: (to, navigationState) => {
        const full =
          typeof to === 'string' ? to : composeFullFromObject(to, pathnameRef.current);
        handleNavigate(full, navigationState);
      },
      encodeLocation: to => {
        const full =
          typeof to === 'string' ? to : composeFullFromObject(to, pathnameRef.current);
        return new URL(full, 'http://_') as unknown as Location;
      },
    }),
    [handleNavigate]
  );

  const location = useMemo<Location>(
    () => ({
      pathname: parsed.pathname,
      search: parsed.search,
      hash: parsed.hash,
      // Both used to be constants — `null` and the literal `'pane'` — so every
      // pane tab reported the same key and no tab ever carried its state.
      state: state ?? null,
      key: locationKey ?? tabId,
    }),
    [parsed, state, locationKey, tabId]
  );

  const navigationContext = useMemo(
    // useTransitions added to NavigationContextObject in react-router 7.18;
    // false keeps this synthetic provider's navigation free of startTransition.
    () => ({ basename, navigator, static: false, future: {}, useTransitions: false }),
    [navigator]
  );

  const locationContext = useMemo(
    () => ({ location, navigationType: 'POP' as NavigationType }),
    [location]
  );

  // Populate matches so useParams() returns the resolved params.
  // useParams reads matches[matches.length - 1].params.
  // Use parsed.pathname (not the raw full path) so relative-routing helpers
  // (resolvePath, useResolvedPath, useHref) don't inherit '?...' in their
  // pathname matching.
  const routeContext = useMemo(
    () => ({
      outlet: null,
      matches: [
        {
          params,
          pathname: parsed.pathname,
          pathnameBase: parsed.pathname,
          route: { path: parsed.pathname },
        },
      ],
      isDataRoute: false,
    }),
    [params, parsed.pathname]
  );

  return useMemo(
    () => ({ navigation: navigationContext, location: locationContext, route: routeContext }),
    [navigationContext, locationContext, routeContext]
  );
}

